import vid from "../../assets/video/system-logistic-homepage-scroll.mp4.asset.json";
import { routes } from "../../lib/nav";
import { SITE_URL } from "../../lib/site";
import type { CaseStudy } from "./types";

const dir = "/cases/system-logistic";

const img = (
  file: string,
  alt: string,
  span: 2 | 3 | 6 = 3,
  width = 1920,
  height = 1246,
) => ({
  src: `${dir}/${file}.webp`,
  src900: `${dir}/${file}-900.webp`,
  alt,
  width,
  height,
  wide: span === 6,
  span,
});

const mobile = (file: string, alt: string) => ({
  src: `${dir}/${file}.webp`,
  src900: `${dir}/${file}-900.webp`,
  alt,
  width: 1920,
  height: 1920,
  span: 2 as const,
});

export const systemLogistic: CaseStudy = {
  slug: "system-logistic",
  title: "System Logistic",
  cardTitle: "Комплексное продвижение логистической компании System",
  subtitle: "Международная логистика и грузоперевозки",
  industry: { label: "Логистика", slug: "logistics" },
  eyebrow: "Сайт и продвижение · Логистика",
  h1: "Сайт и продвижение для международной логистической компании System",
  lead:
    "Разработали сайт и выстроили digital-привлечение для компании, которая организует международные перевозки из Европы, Китая, Турции и других направлений.",
  heroButtons: { site: "Смотреть сайт", discuss: "Обсудить продвижение" },

  seo: {
    primaryKeyword: "продвижение логистической компании",
    secondaryKeywords: [
      "комплексное продвижение логистической компании",
      "маркетинг транспортной компании",
      "разработка сайта логистической компании",
      "создание сайта транспортной компании",
      "SEO для логистической компании",
      "Яндекс Директ для грузоперевозок",
      "продвижение международных грузоперевозок",
      "реклама транспортно-логистической компании",
    ],
    semanticEntities: [
      "логистическая компания",
      "международные грузоперевозки",
      "посадочные страницы направлений",
      "калькулятор стоимости доставки",
      "SEO-структура сайта",
      "Яндекс Директ",
      "техническая поддержка сайта",
    ],
    excludedKeywords: [
      "доставка грузов из Китая",
      "грузоперевозки из Турции",
      "доставка мебели",
      "международные перевозки",
      "стоимость доставки груза",
    ],
    title: "Продвижение логистической компании System — кейс HAY",
    description:
      "Кейс HAY.agency: сайт, SEO, Яндекс Директ и техническая поддержка международной логистической компании System.",
    h1: "Сайт и продвижение для международной логистической компании System",
    slug: "system-logistic",
    canonicalUrl: `${SITE_URL}/cases/system-logistic`,
    ogTitle: "Продвижение логистической компании System — кейс HAY",
    ogDescription:
      "Сайт с системой направлений, калькулятор стоимости, SEO и Яндекс Директ для международной логистики.",
    ogImage: `${SITE_URL}${dir}/system-logistic-og.webp`,
    robotsIndex: true,
    datePublished: "2023-04-01",
    dateModified: "2026-08-29",
  },

  links: {
    relatedService: {
      label: "SEO-продвижение сайтов",
      to: routes.seo,
      note: "Структура посадочных страниц и рост органического спроса",
    },
    relatedIndustry: {
      label: "Ведение Яндекс Директа",
      to: routes.context,
      note: "Разделение рекламы по направлениям перевозок",
    },
    relatedCases: [
      {
        label: "Комплексное продвижение строительной компании ESG",
        to: "/cases/esg",
        note: "Сайт, SEO и Директ для нескольких направлений бизнеса",
      },
      {
        label: "Сайт-портфолио архитектурного бюро Archidica",
        to: "/cases/archidica",
        note: "Проектирование структуры и каталога проектов",
      },
      {
        label: "Сайт-каталог для производителя заборов Artline",
        to: "/cases/artline",
        note: "B2B-проект: каталог, расчёт и заявки из поиска",
      },
    ],
    relatedArticles: [],
  },

  cta: {
    title: "Нужно продвигать логистическую компанию?",
    text:
      "Разработаем сайт, разделим спрос по направлениям и объединим SEO, Яндекс Директ и аналитику.",
    button: "Обсудить продвижение",
  },

  client: {
    name: "System Logistic",
    url: "https://system-logistic.ru/",
    city: "Москва",
    industry: "Международная логистика и грузоперевозки",
  },
  year: "Долгосрочное сотрудничество",
  services: [
    "Аналитика и проектирование",
    "UX/UI-дизайн",
    "Разработка сайта",
    "SEO",
    "Яндекс Директ",
    "Техническая поддержка",
  ],
  platform: "Корпоративный сайт с калькулятором стоимости",
  tags: [
    { label: "Корпоративный сайт", to: routes.corporate },
    { label: "SEO", to: routes.seo },
    { label: "Яндекс Директ", to: routes.context },
  ],
  facts: [
    { label: "Клиент", value: "System Logistic" },
    { label: "Отрасль", value: "Международная логистика и грузоперевозки" },
    { label: "Тип проекта", value: "Корпоративный сайт и комплексное продвижение" },
    { label: "Услуги", value: "Сайт, SEO, Яндекс Директ, поддержка" },
  ],

  results: {
    title: "Что реализовали",
    items: [
      "Корпоративный сайт логистической компании",
      "Структура посадочных страниц по направлениям и типам грузов",
      "Калькулятор предварительной стоимости доставки",
      "SEO и Яндекс Директ в одной системе спроса",
      "Постоянное развитие проекта и техническая поддержка",
    ],
  },

  story: [
    {
      title: "Задача",
      paragraphs: [
        "Логистическая компания продаёт не одну услугу, а десятки сценариев: страна отправления, тип груза, вид транспорта и дополнительные услуги вроде таможенного оформления или хранения. Общая страница «международные перевозки» такой спрос не закрывает.",
        "Нужна была digital-система: сайт, который отвечает на конкретный запрос, и каналы привлечения, которые ведут пользователя именно на этот ответ.",
      ],
    },
  ],
  features: [],

  design: {
    title: "Интерфейс, в котором понятно, что делать дальше",
    paragraphs: [
      "Первый экран сразу показывает направления и виды транспорта, а не абстрактную презентацию компании. Дальше пользователь попадает либо в нужное направление, либо в калькулятор стоимости.",
      "Схема перевозки, этапы работы и услуги оформлены короткими блоками — так клиент видит маршрут груза и зону ответственности перевозчика без длинных текстов.",
    ],
    bullets: [
      "Направления и виды транспорта на первом экране",
      "Калькулятор стоимости как основное целевое действие",
      "Короткие блоки вместо длинных описаний услуг",
    ],
  },

  identity: {
    fonts: [
      { name: "Гротеск Bold", role: "Заголовки и цифры", sample: "Международные перевозки" },
      { name: "Гротеск Regular", role: "Текст интерфейса", sample: "Расчёт стоимости доставки" },
    ],
    colors: [
      { hex: "#0B1A2B", name: "Основной тёмный" },
      { hex: "#1F4E79", name: "Акцент навигации" },
      { hex: "#F3F5F8", name: "Фон блоков" },
      { hex: "#D0021B", name: "Акцент действия" },
    ],
  },

  cover: img(
    "system-logistic-case-cover",
    "Сайт логистической компании System: главная страница, направления перевозок и калькулятор стоимости",
    6,
    1920,
    1080,
  ),

  coverVideo: vid.url,
  coverPoster: {
    src: `${dir}/system-logistic-homepage-scroll-poster.webp`,
    src900: `${dir}/system-logistic-homepage-scroll-poster-900.webp`,
    alt: "Прокрутка главной страницы сайта логистической компании System",
    width: 1920,
    height: 1080,
    span: 6,
    wide: true,
  },

  gallery: [
    img(
      "system-logistic-turkey-direction",
      "Страница направления перевозок из Турции с видами транспорта и типами грузов",
      3,
    ),
    img(
      "system-logistic-china-direction",
      "Страница направления перевозок из Китая",
      3,
    ),
    img(
      "system-logistic-calculator-filled",
      "Калькулятор предварительной стоимости доставки с заполненными параметрами",
      6,
    ),
  ],

  responsive: {
    text: [
      "Значительная часть обращений приходит с мобильных: пользователь ищет перевозчика в дороге и хочет быстро получить ориентир по стоимости и срокам.",
      "В мобильной версии направления, виды транспорта и форма расчёта доступны с первого экрана, а шаги калькулятора разбиты так, чтобы их можно было пройти одной рукой.",
    ],
    images: [
      mobile("system-logistic-mobile-directions", "Мобильный блок направлений перевозок"),
      mobile("system-logistic-mobile-calculator", "Мобильный калькулятор стоимости доставки"),
    ],
  },

  columnBlocks: [
    {
      id: "structure",
      title: "Сайт под разные направления логистического спроса",
      paragraphs: [
        "Клиенты ищут перевозчика по стране отправления, типу груза, способу доставки и дополнительным услугам. Поэтому сайт построен не вокруг одной общей страницы, а вокруг системы отдельных направлений.",
      ],
      columns: [
        {
          title: "Точки входа",
          items: ["Направления перевозок", "Виды транспорта", "Типы грузов", "Дополнительные услуги"],
        },
        {
          title: "Примеры страниц",
          items: ["Перевозки из Турции", "Перевозки из Китая", "Перевозки по Европе", "Доставка мебели"],
        },
        {
          title: "Целевое действие",
          items: ["Калькулятор стоимости", "Быстрый расчёт на странице", "Заявка менеджеру"],
        },
      ],
      images: [
        img("system-logistic-europe-direction", "Страница направления перевозок по Европе с вариантами доставки", 3),
        img("system-logistic-sea-freight", "Страница морских перевозок с описанием маршрутов", 3),
        img("system-logistic-furniture-delivery", "Страница доставки мебели с отдельной формой расчёта", 3),
        img("system-logistic-calculator-filled", "Пошаговая форма расчёта стоимости доставки", 3),
      ],
      imageCols: 2,
      caption:
        "Для каждого направления создана отдельная точка входа с релевантной услугой и формой расчёта.",
    },
    {
      id: "growth",
      title: "Привлекаем спрос и развиваем сайт после запуска",
      paragraphs: [
        "SEO и реклама работают на одну структуру: запрос ведёт на страницу своего направления, а страница — на расчёт стоимости.",
      ],
      columns: [
        {
          title: "SEO",
          items: [
            "Страницы по странам и типам перевозок",
            "Страницы по грузам и доп. услугам",
            "Техническая оптимизация",
            "Внутренняя перелинковка",
          ],
        },
        {
          title: "Яндекс Директ",
          items: [
            "Разделение по направлениям",
            "Объявление → своя посадочная",
            "Никаких переходов на общую главную",
          ],
        },
        {
          title: "Техническая поддержка",
          items: [
            "Новые услуги и направления",
            "Обновление расчётов и контента",
            "Контроль форм и стабильности",
          ],
        },
      ],
      images: [
        img("system-logistic-additional-services", "Дополнительные услуги и складские решения на посадочной странице", 3),
        img("system-logistic-logistics-scheme", "Схема грузоперевозки: этапы работы с грузом", 3),
      ],
      imageCols: 2,
      caption: "Схема простая: запрос → страница направления → расчёт стоимости → обращение.",
    },
  ],

  servicesDone: {
    title: "Что сделали в проекте",
    items: [
      {
        label: "Разработка корпоративного сайта",
        to: routes.corporate,
        note: "Проектирование, дизайн и разработка сайта с калькулятором",
      },
      {
        label: "SEO-продвижение",
        to: routes.seo,
        note: "Структура посадочных страниц, техническая оптимизация, перелинковка",
      },
      {
        label: "Ведение Яндекс Директа",
        to: routes.context,
        note: "Реклама по направлениям перевозок с релевантными посадочными",
      },
      {
        label: "Техническая поддержка",
        to: routes.uslugi,
        note: "Развитие сайта, новые направления и контроль форм",
      },
    ],
  },
};
